import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Calendar, Users, CreditCard, BarChart3, FileText, Sparkles, Store, Bell, Shield } from 'lucide-react';
import { useOnboarding } from '../../context/OnboardingContext';
import { useLocation } from 'react-router-dom';

const WelcomeModal = () => {
  const { showWelcomeModal, markWelcomeSeen, userType, loading } = useOnboarding();
  const [currentStep, setCurrentStep] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();
  
  const isEO = userType === 'EO' || location.pathname.startsWith('/eo');

  useEffect(() => {
    let timer;
    if (showWelcomeModal && !isClosing && !loading) {
      timer = setTimeout(() => setIsVisible(true), 100);
    } else if (!showWelcomeModal && !isClosing) {
      setIsVisible(false);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showWelcomeModal, isClosing, loading]);

  const handleClose = async () => {
    setIsClosing(true);
    setIsVisible(false);
    await new Promise(resolve => setTimeout(resolve, 300));
    await markWelcomeSeen();
    setIsClosing(false);
  };

  if (loading) return null;
  if (!showWelcomeModal && !isClosing) return null;

  const eoSteps = [
    {
      icon: Sparkles,
      title: "Welcome to Pebble!",
      subtitle: "Your Event Business Starts Here",
      description: "Post events, collect booth fees, and manage your vendors all in one place. Let us show you around!",
      image: null,
      highlight: "pebble-red"
    },
    {
      icon: Calendar,
      title: "Post Your Event",
      subtitle: "Simple & Quick",
      description: "Create your market, fair, or festival in minutes. Set your booth fees and open registration for vendors to apply.",
      tips: ["Post events in under 5 minutes", "Set your own booth fees", "Use AI to create listings instantly"]
    },
    {
      icon: Users,
      title: "Approve Vendors, Get Paid",
      subtitle: "You're In Control",
      description: "See who wants to join your event. Approve vendors with one tap. They pay, you earn. It's that simple.",
      tips: ["Review vendor applications", "One-tap approvals", "Collect required documents automatically"]
    },
    {
      icon: CreditCard,
      title: "Collect Booth Fees",
      subtitle: "Secure & Automatic",
      description: "When vendors register, their payment goes straight to your bank. A small service fee is deducted automatically.",
      tips: ["Vendors pay instantly", "Money goes to your bank", "Track every payment in real time"]
    },
    {
      icon: BarChart3,
      title: "See Your Earnings",
      subtitle: "Pro Feature",
      description: "Track how much you've earned, which events perform best, and grow your vendor network.",
      tips: ["Revenue reports by event", "Vendor performance insights", "Plan smarter future events"],
      isPro: true
    },
    {
      icon: FileText,
      title: "You're Ready!",
      subtitle: "Post Your First Event",
      description: "Head to your dashboard and create your first event. Vendors are waiting to join your marketplace!",
      cta: "Let's Go"
    }
  ];

  const mpsSteps = [
    {
      icon: Sparkles,
      title: "Welcome to Pebble!",
      subtitle: "Find Your Next Event",
      description: "Discover amazing events and grow your business. Let us show you how to get started!",
      highlight: "pebble-red"
    },
    {
      icon: Store,
      title: "Browse Events",
      subtitle: "Find Your Perfect Fit",
      description: "Explore upcoming events in your area. Filter by date, location, and category to find events that match your business.",
      tips: ["Browse local events", "Filter by category", "See event details and fees"]
    },
    {
      icon: FileText,
      title: "Apply with Ease",
      subtitle: "Quick Application Process",
      description: "Apply to events with just a few clicks. Upload your documents once and reuse them for future applications.",
      tips: ["One-click applications", "Reusable document uploads", "Track application status"]
    },
    {
      icon: CreditCard,
      title: "Secure Payments",
      subtitle: "Pay Registration Fees",
      description: "Pay event registration fees securely through Stripe. Get instant confirmation and receipts for your records.",
      tips: ["Secure checkout", "Instant confirmation", "Digital receipts"]
    },
    {
      icon: Bell,
      title: "Stay Updated",
      subtitle: "Never Miss an Opportunity",
      description: "Get notified about new events, application updates, and important announcements from event organizers.",
      tips: ["Event notifications", "Application status updates", "Organizer announcements"]
    },
    {
      icon: Shield,
      title: "You're Ready!",
      subtitle: "Start Exploring Events",
      description: "Browse available events and submit your first application. Build your vendor profile to stand out!",
      cta: "Explore Events"
    }
  ];

  const steps = isEO ? eoSteps : mpsSteps;
  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      handleClose();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const IconComponent = currentStepData.icon;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${isVisible ? 'bg-black/50 backdrop-blur-sm' : 'bg-transparent'}`}>
      <div className={`relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'}`}>
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors z-10"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="p-8">
          <div className="flex justify-center mb-6">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${currentStepData.isPro ? 'bg-gradient-to-br from-purple-500 to-indigo-600' : 'bg-gradient-to-br from-red-500 to-red-600'}`}>
              <IconComponent className="h-8 w-8 text-white" />
            </div>
          </div>

          <div className="text-center mb-6">
            {currentStepData.isPro && (
              <span className="inline-block px-2 py-1 mb-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-xs font-medium rounded-full">
                PRO FEATURE
              </span>
            )}
            <h2 className="text-2xl font-bold text-gray-900 mb-1">{currentStepData.title}</h2>
            <p className="text-sm font-medium text-pebble-red">{currentStepData.subtitle}</p>
          </div>

          <p className="text-gray-600 text-center mb-6 leading-relaxed">
            {currentStepData.description}
          </p>

          {currentStepData.tips && (
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <ul className="space-y-2">
                {currentStepData.tips.map((tip, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm text-gray-700">
                    <div className="w-1.5 h-1.5 rounded-full bg-pebble-red flex-shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center justify-center gap-1.5 mb-6">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentStep 
                    ? 'w-6 bg-pebble-red' 
                    : index < currentStep 
                      ? 'bg-pebble-red/50' 
                      : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between gap-3">
            <button
              onClick={handlePrev}
              disabled={isFirstStep}
              className={`flex items-center gap-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isFirstStep 
                  ? 'text-gray-300 cursor-not-allowed' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </button>

            <button
              onClick={handleClose}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              Skip Tour
            </button>

            <button
              onClick={handleNext}
              className="flex items-center gap-1 px-6 py-2.5 bg-pebble-red text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors"
            >
              {isLastStep ? (currentStepData.cta || 'Get Started') : 'Next'}
              {!isLastStep && <ChevronRight className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
